import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { BigNumber } from "bignumber.js";
import { Modal } from "react-responsive-modal";
import { Context } from "../../context";
import { Badge, BadgeType } from "@merchstores/shared/elements/Badge";
import { Card } from "@merchstores/shared/elements/Card";
import { Logo } from "@merchstores/shared/elements/Logo";
import { Table } from "@merchstores/shared/elements/Table";
import { cloudinaryApplyWidthHeight } from "@merchstores/shared/components/Cloudinary";
import { getLocalDateText } from "../DateTime";
import LoadingGif from "../../assets/common/Flask-3s-200px.svg";
import { IMerchStoreProps } from "./index";
import { buildColumns } from "./MerchStoreListColumnOptions";
import { buildDropdowns } from "./MerchStoreListDropdownOptions";
import { AdminHeader } from "../AdminHeader";
import ClosedMerchStoreDropdown from "./ClosedMerchStoreDropdown";
import "./styles.scss";
import { CTA } from "@merchstores/shared/elements/Cta";
import { toast } from "react-toastify";
import { MdStore } from "react-icons/md";
import { CreateMerchstore } from "../CreateMerchstore";
import { useHistory } from "react-router-dom";
import { ActionDropdown } from "@merchstores/shared/elements/ActionDropdown";
import { ReassignMerch } from "../ReassignMerch";

export const MerchStoreList = () => {
  const { userRole, userEmail } = useContext(Context);
  const [merchstores, setMerchStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [newMerchstoreModalOpen, setNewMerchstoreModalOpen] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [currentMerch, setCurrentMerch] = useState("");
  const [selectedMerchstoreCode, setSelectedMerchstoreCode] = useState("");
  const history = useHistory();

  const iconSize = isMobile ? '16px' : '20px';

  const fetchMerchstores = async () => {
    axios
      .post("/.netlify/functions/fetchMerchstores", {
        email: userEmail,
        role: userRole,
      })
      .then((res) => {
        const merchstoreData = res.data.map((merchstore: IMerchStoreProps) => {
          let formattedRevenue = "$0.00";
          if (merchstore.revenue) {
            const revenueString = new BigNumber(merchstore.revenue.$numberDecimal);
            formattedRevenue = `$${revenueString.toFormat(2)}`
          }
          const numOrders = merchstore?.members?.filter(
            (member: any) => member.orderId
          ).length;
          const redirectAction = () => {
            history.push(`/admin/merchstores/${merchstore.storeCode}`);
          }
          const reassignAction = () => {
            setSelectedMerchstoreCode(merchstore.storeCode);
            setCurrentMerch(merchstore.merchologist);
            setReassignModalOpen(true);
          }
          let dropDownOptions: any = buildDropdowns(userRole, merchstore, reassignAction, redirectAction);
          // takes the existing default menu and replaces with a new one if the order
          // is closed 
          dropDownOptions = ClosedMerchStoreDropdown.resolveDropdownOptions(
              merchstore, userRole, userEmail, history, dropDownOptions
          );

          const data: any = {};
          const optimizedLogo = cloudinaryApplyWidthHeight(
            merchstore.storeLogo,
            "72",
            "72"
          );
          data.logo = {
            desktopOnly: false,
            value: (
                <a
                  href={`/admin/merchstores/${merchstore.storeCode}`}
                  className=""
                >
                  {optimizedLogo ? <Logo size="small" imgUrl={optimizedLogo} /> : <></>}
                  <div className="text-left pl-3 font-bold text-merch-dark-gray pt-3 w-200">
                    {merchstore.name}
                  </div>
                </a>
            ),
          };
          data.groupAdmin = {
            desktopOnly: true,
            value: (
              <div className="text-left pl-10 font-bold text-merch-dark-gray">
                {merchstore.customerAdmins ? merchstore.customerAdmins[0] : ""}
              </div>
            ),
          };
          if (userRole !== "MERCHOLOGIST" && userRole !== "ADMIN") {
            data.assignedTo = {
              desktopOnly: true,
              value: (
                <div className="text-left font-bold text-merch-dark-gray opacity-50">
                  {merchstore.merchologist}
                </div>
              ),
            };
          }
          if (userRole === "MERCHOLOGIST" || userRole === "ADMIN") {
            data.storeId = {
              desktopOnly: true,
              value: (
                <div className="text-left font-bold text-merch-dark-gray opacity-50">
                  {merchstore.storeCode}
                </div>
              )
            }
          }
          data.orderCount = {
            desktopOnly: true,
            value: (
              <div className="font-bold text-merch-dark-gray">
                {numOrders}
              </div>
            ),
          };
          data.revenue = {
            desktopOnly: true,
            value: (
              <div className="font-bold text-merch-dark-gray">
                {formattedRevenue}
              </div>
            ),
          };
          data.closeDate = {
            desktopOnly: true,
            value: (
              <div className="font-bold text-merch-dark-gray">
                {getLocalDateText(merchstore.nextProcessingDate)}
              </div>
            ),
          };
          data.status = {
            desktopOnly: false,
            value: (
              <Badge className="no-text-mobile" type={merchstore.status as BadgeType}>
                {merchstore.status}
              </Badge>
            ),
            
          };
        // TODO: Implement the action dropdown (see GroupOrders component)
        data.actions = {
          desktopOnly: false,
          value: <ActionDropdown title="Actions" list={dropDownOptions} />
          };
          
          
          return {
            tr: {
              class: `${merchstore.status}`,
              data,
            },
          };
        });
        setMerchStores(merchstoreData);
        if (merchstores) {
          setTimeout(() => setLoading(false), 500);
        }
      })
      .catch((err: any) => {
        console.error(err);
        toast.error(
          "There was an error fetching the Merchstore data. Please try again or contact support."
        );
      });
  };

  useEffect(() => {
    if (userRole && userEmail) {
      fetchMerchstores();
    }
  }, [userRole, userEmail]);

  const setReassignModalClose = () => {
    setReassignModalOpen(false);
  }

  const columns = buildColumns(userRole);

  if (loading)
    return (
      <div className="h-screen flex items-center justify-center">
        <img src={LoadingGif} alt="loading flask" width="100px" />
      </div>
    );

  return (
    <>
      <AdminHeader title="MerchStores" subtitle={getLocalDateText(new Date().toISOString())} >
        {userRole !== "ADMIN" &&
          <div>
            <CTA disabled={false} size="standard" type="primary" icon={<MdStore size={iconSize} />} mobileIconView={true}
              onClick={() => setNewMerchstoreModalOpen(true)}>
              {"New MerchStore"}
            </CTA>
          </div>
        }
      </AdminHeader>

      <div className="merchstores-container">
        <Card data-class="merchstores">
          <Table
            itemsName="merchstores"
            name="merchstores"
            filter={true}
            columns={columns}
            data={merchstores}
            elementsPerPage={50}
          />
        </Card>
      </div>
      <CreateMerchstore modalOpen={newMerchstoreModalOpen} onModalClose={() => setNewMerchstoreModalOpen(false)} />
      <Modal open={reassignModalOpen} onClose={setReassignModalClose} center classNames={{ modal: "reassign-merch-modal" }}>
          <ReassignMerch
              currentMerch={currentMerch}
              setCurrentMerch={setCurrentMerch}
              currentOrderId={selectedMerchstoreCode}
              open={open}
              setOpen={setReassignModalOpen}
              isMerchstore={true}
              fetchMerchstores={fetchMerchstores} />
      </Modal>
    </>
  );
};
